/**
 * Portal-side hook for the Playground User Explorer impersonation flow.
 *
 * When the portal is rendered inside a playground iframe, this hook listens
 * for impersonation tokens posted by the playground and signs them in. It
 * never calls the Cloud Function itself — the playground does that from its
 * admin-authenticated session and ferries the minted token in.
 *
 * Subsequent tokens (the admin picking a different user in the playground)
 * arrive on the same listener; the hook signs the new user in without
 * unmounting the portal app. Sign-ins are serialized through a single
 * pipeline and tagged with a generation counter — if a newer token arrives
 * while an older one is mid-flight, the older one is dropped (no Firebase
 * sign-in / state churn for the abandoned user).
 *
 * The hook has no render output — the playground overlays the iframe with
 * its own "Impersonating …" spinner until the handshake completes, so any
 * router churn that happens here (e.g. the initial `/` → `/dashboard`
 * redirect) is never visible to the admin.
 *
 * Outside of an iframe (i.e. regular portal users) the effect short-circuits.
 */
import { isUserScopedQuery, queryClient } from '@portal/queryClient'
import { router } from '@portal/router'
import { isInIframe } from '@zealth/core/env/isInIframe'
import { useAuthStore } from '@zealth/core/firebase/authStore'
import { getAuthInstance } from '@zealth/core/firebase/runtime'
import { signInWithCustomToken } from 'firebase/auth'
import { useEffect } from 'react'

import { isTrustedPlaygroundOrigin } from './playgroundOrigin'

interface ImpersonationMessage {
  type: 'impersonation:token'
  token: string
}

const isImpersonationMessage = (data: unknown): data is ImpersonationMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === 'impersonation:token' &&
  typeof (data as { token?: unknown }).token === 'string'

// signInWithCustomToken resolves before Firebase's onAuthStateChanged listener
// has fanned out to our auth store. Without this wait, the very next route
// guard (`requireAuth` reads the store) sees `user === null` and redirects to
// `/signin`. Block until the store reflects the freshly-signed-in uid.
const waitForAuthStoreUid = (uid: string): Promise<void> =>
  new Promise(resolve => {
    if (useAuthStore.getState().user?.uid === uid) return resolve()
    const unsubscribe = useAuthStore.subscribe(
      s => s.user?.uid,
      currentUid => {
        if (currentUid === uid) {
          unsubscribe()
          resolve()
        }
      }
    )
  })

export const useImpersonationListener = (): void => {
  const inIframe = isInIframe()

  useEffect(() => {
    if (!inIframe) return

    // Announce readiness so the playground can ferry the token. The parent
    // origin is unknown to us; the inbound listener enforces the strict origin
    // pattern below — sending `*` here leaks only the literal string
    // "impersonation:ready", which is not sensitive.
    window.parent.postMessage({ type: 'impersonation:ready' }, '*')

    // Latest generation, incremented per inbound token. Stale generations are
    // silently dropped — keeps rapid user-switching safe without racing
    // Firebase auth state.
    let latest = 0
    // Single-flight pipeline: sign-ins are awaited end-to-end. A new token
    // doesn't queue work on top of an in-flight one — it bumps `latest`, the
    // current step checks it and bails, and the new step takes over.
    let pipeline: Promise<void> = Promise.resolve()

    const handleToken = (token: string, origin: string): void => {
      const gen = ++latest
      pipeline = pipeline
        .catch(() => undefined)
        .then(async () => {
          if (gen !== latest) return

          const credential = await signInWithCustomToken(
            getAuthInstance(),
            token
          )
          if (gen !== latest) return

          const tokenResult = await credential.user.getIdTokenResult()
          if (gen !== latest) return

          const readOnly = tokenResult.claims.readOnly === true

          await waitForAuthStoreUid(credential.user.uid)
          if (gen !== latest) return

          // 1. Run route loaders against the new user (App.tsx's subscriber
          //    also kicks off an invalidate; this awaits the same work).
          await router.invalidate()
          if (gen !== latest) return

          // 2. Each new user lands on /dashboard — avoids inheriting a
          //    sub-route (e.g. /surveys/...) that may be invalid for the
          //    next user. Awaiting `router.navigate` ensures the dashboard
          //    component is mounted before the resetQueries below, so its
          //    observers are part of the awaited refetch set.
          if (window.location.pathname !== '/dashboard') {
            await router.navigate({ to: '/dashboard', replace: true })
            if (gen !== latest) return
          }

          // 3. Wait for every active observer's refetch to settle. The
          //    App.tsx subscriber already kicked off `resetQueries` when the
          //    auth uid flipped, so those fetches are in flight; this call
          //    awaits them without cancelling (`cancelRefetch: false`) and
          //    without re-triggering anything. Posting `impersonation:
          //    established` only after this point keeps the playground's
          //    "Impersonating …" overlay up until the dashboard is fully
          //    loaded, so the admin sees one continuous loading state
          //    instead of "overlay → in-app spinner → dashboard".
          await queryClient.invalidateQueries(
            { predicate: isUserScopedQuery, refetchType: 'active' },
            { cancelRefetch: false }
          )
          if (gen !== latest) return

          window.parent.postMessage(
            { type: 'impersonation:established', readOnly },
            origin
          )
        })
        .catch(err => {
          if (gen !== latest) return
          // eslint-disable-next-line no-console
          console.error('Impersonation sign-in failed:', err)
          router.navigate({ to: '/signin', replace: true })
        })
    }

    const onMessage = (event: MessageEvent): void => {
      if (!isImpersonationMessage(event.data)) return
      if (!isTrustedPlaygroundOrigin(event.origin)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[useImpersonationListener] Rejected impersonation token from untrusted origin: ${event.origin}. ` +
            `If this is a legitimate playground host, add it to isTrustedPlaygroundOrigin().`
        )
        return
      }
      handleToken(event.data.token, event.origin)
    }

    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('message', onMessage)
      // Anything still in-flight is implicitly stale (the hook is unmounting,
      // typically only during hot-reload in dev).
      latest = -1
    }
  }, [inIframe])
}
